import { Call, Device } from "@twilio/voice-sdk";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { createStore } from "./utils";

// Device state enum matching Twilio SDK
export type DeviceState = "unregistered" | "registering" | "registered" | "error";

// Call status during lifecycle
export type CallStatus = "idle" | "connecting" | "ringing" | "open" | "disconnected";

// Call direction
export type CallDirection = "inbound" | "outbound";

// Conference state
export interface ConferenceState {
  id: string;
  users: string[];
  phones: string[];
  enduserName: string;
}

// Warm transfer state
export interface WarmTransferState {
  isActive: boolean;
  targetUserId: string | null;
  targetUserName: string | null;
}

// Call metadata for UI display during active call
export interface ActiveCallMeta {
  enduserId: string | null;
  enduserName: string | null;
  phoneNumber: string | null;
  orgNumber: string | null; // Organization's Twilio number (from number for outbound, to number for inbound)
  startTime: Date | null;
}

// Dialer mode — phone call vs. ad-hoc video call
export type DialerMode = "phone" | "video";

// Dialer window state
export interface DialerState {
  isDialerOpen: boolean;
  dialerEnduser: TellescopeModelFromName<"endusers"> | null;
  dialerMode: DialerMode;
}

// Dialer window actions
export interface DialerActions {
  openDialer: (options?: { enduser?: TellescopeModelFromName<"endusers">; mode?: DialerMode }) => void;
  closeDialer: () => void;
}

// Calling slice state
export interface CallingState {
  // Device state
  device: Device | null;
  deviceState: DeviceState;
  connectionError: string | null;

  // Active call state
  activeCall: Call | null;
  callStatus: CallStatus;
  callDirection: CallDirection | null;
  isMuted: boolean;

  // Incoming calls queue
  incomingCalls: Call[];

  // Registered by useTwilioDevice so useCallOperations can stop the ring on reject/ignore
  _stopIncomingRingFn: (() => void) | null;

  // Conference state
  conference: ConferenceState | null;

  // Warm transfer state
  warmTransfer: WarmTransferState;

  // Call metadata (for UI display during call)
  activeCallMeta: ActiveCallMeta;
}

// Calling slice actions
export interface CallingActions {
  // Device management
  setDevice: (device: Device | null) => void;
  setDeviceState: (state: DeviceState) => void;
  setConnectionError: (error: string | null) => void;

  // Call management
  setActiveCall: (call: Call | null) => void;
  setCallStatus: (status: CallStatus) => void;
  setCallDirection: (direction: CallDirection | null) => void;
  setMuted: (muted: boolean) => void;

  // Incoming calls
  addIncomingCall: (call: Call) => void;
  removeIncomingCall: (call: Call) => void;
  clearIncomingCalls: () => void;

  // Incoming ring control — registered by useTwilioDevice, called by useCallOperations
  registerStopIncomingRing: (fn: () => void) => void;
  stopIncomingRing: () => void;

  // Conference
  setConference: (conference: ConferenceState | null) => void;
  updateConferenceAttendees: (users: string[], phones: string[]) => void;

  // Warm transfer
  setWarmTransfer: (transfer: Partial<WarmTransferState>) => void;
  clearWarmTransfer: () => void;

  // Metadata
  setActiveCallMeta: (meta: Partial<ActiveCallMeta>) => void;
  clearActiveCallMeta: () => void;

  // Reset
  resetCallState: () => void;
  reset: () => void;
}

// Combined slice interface
export interface CallingStore extends DialerState, DialerActions, CallingState, CallingActions {}

const initialDialerState: DialerState = {
  isDialerOpen: false,
  dialerEnduser: null,
  dialerMode: "phone",
};

// Initial state
const initialState: CallingState = {
  // Device state
  device: null,
  deviceState: "unregistered",
  connectionError: null,

  // Active call state
  activeCall: null,
  callStatus: "idle",
  callDirection: null,
  isMuted: false,

  // Incoming calls queue
  incomingCalls: [],

  _stopIncomingRingFn: null,

  // Conference state
  conference: null,

  // Warm transfer state
  warmTransfer: {
    isActive: false,
    targetUserId: null,
    targetUserName: null,
  },

  // Call metadata
  activeCallMeta: {
    enduserId: null,
    enduserName: null,
    phoneNumber: null,
    orgNumber: null,
    startTime: null,
  },
};

export const { useStore: useCallingStore, useShallowStore: useCallingShallowStore } = createStore<CallingStore>(
  (set) => ({
    ...initialDialerState,
    ...initialState,
    openDialer: ({ enduser, mode = "phone" } = {}) =>
      set({ isDialerOpen: true, dialerEnduser: enduser ?? null, dialerMode: mode }),
    closeDialer: () => set({ isDialerOpen: false, dialerEnduser: null, dialerMode: "phone" }),
    setDevice: (device) => set({ device }),

    setDeviceState: (deviceState) => set({ deviceState, connectionError: null }),

    setConnectionError: (connectionError) => set({ connectionError, deviceState: "error" }),

    // Call management
    setActiveCall: (activeCall) => set({ activeCall }),

    setCallStatus: (callStatus) => set({ callStatus }),

    setCallDirection: (callDirection) => set({ callDirection }),

    setMuted: (isMuted) => set({ isMuted }),

    // Incoming calls (deduplicated by CallSid)
    addIncomingCall: (call) =>
      set((state) => {
        const callSid = call.parameters?.CallSid;
        if (callSid && state.incomingCalls.some((c) => c.parameters?.CallSid === callSid)) {
          return state;
        }
        return { incomingCalls: [call, ...state.incomingCalls] };
      }),

    removeIncomingCall: (call) =>
      set((state) => {
        const callSid = call.parameters?.CallSid;
        return {
          incomingCalls: state.incomingCalls.filter((c) => (callSid ? c.parameters?.CallSid !== callSid : c !== call)),
        };
      }),

    clearIncomingCalls: () => set({ incomingCalls: [] }),

    registerStopIncomingRing: (fn) => set({ _stopIncomingRingFn: fn }),
    stopIncomingRing: () =>
      set((state) => {
        state._stopIncomingRingFn?.();
        return {};
      }),

    // Conference
    setConference: (conference) => set({ conference }),

    updateConferenceAttendees: (users, phones) =>
      set((state) => {
        const currentConference = state.conference;
        if (!currentConference) return state;

        return {
          conference: { ...currentConference, users, phones },
        };
      }),

    // Warm transfer
    setWarmTransfer: (transfer) =>
      set((state) => ({
        warmTransfer: { ...state.warmTransfer, ...transfer },
      })),

    clearWarmTransfer: () =>
      set({
        warmTransfer: {
          isActive: false,
          targetUserId: null,
          targetUserName: null,
        },
      }),

    // Metadata
    setActiveCallMeta: (meta) =>
      set((state) => ({
        activeCallMeta: { ...state.activeCallMeta, ...meta },
      })),

    clearActiveCallMeta: () =>
      set({
        activeCallMeta: {
          enduserId: null,
          enduserName: null,
          phoneNumber: null,
          orgNumber: null,
          startTime: null,
        },
      }),

    // Reset call state (preserves device and ring control)
    resetCallState: () => {
      set((state) => ({
        ...state,
        ...initialState,
        device: state.device,
        deviceState: state.deviceState,
        _stopIncomingRingFn: state._stopIncomingRingFn,
      }));
    },

    // Full reset (including device)
    reset: () => {
      set((state) => ({
        ...state,
        ...initialState,
      }));
    },
  }),
  { name: "CALLING_STORE" }
);
